import { getTableColumns, getTableName } from "@-/com.npmjs.drizzle-orm";
import { CodecsCollection } from "@-/com.npmjs.drizzle-orm/codecs";
import { expoSQLiteCodecs } from "@-/com.npmjs.drizzle-orm/expo-sqlite/codecs";
import { resolveSQLiteTypeAlias } from "@-/com.npmjs.drizzle-orm/sqlite-core";
import onetime from "@-/com.npmjs.onetime";
import * as z from "@-/com.npmjs.zod";
import { actorSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import { actorDataExportSchema } from "@-/dev.improve.haniverse.lib.actor-data-transfer";

const rowSchema = z.record(z.string(), z.unknown());
const rowsSchema = z.array(rowSchema);

// Since drizzle-orm 1.0.0-rc.5 the driver's codecs decode JSON, boolean, timestamp and blob
// values; `mapFromDriverValue` alone returns JSON columns as their stored text. Raw rows come
// from expo-sqlite (or the web engine's SQLite, which returns the same value types), so decode
// them with the codecs the app's expo-sqlite driver uses, in drizzle's order: codec, then column.
const getSqliteCodecs = onetime(
  () => new CodecsCollection(resolveSQLiteTypeAlias, expoSQLiteCodecs),
);

/** Every actor table, with conversions between raw SQLite column values and export rows. */
export const backupTables = Object.entries(actorSchema).map(([key, table]) => {
  const columns = getTableColumns(table);
  const schema = Reflect.get(actorDataExportSchema.shape.tables.shape, key);
  if (!(schema instanceof z.ZodType)) {
    throw new TypeError(`Missing backup schema for ${key}`);
  }
  return {
    /** Raw SQLite rows (column names, driver values) → export rows (property names, app values). */
    decode: (rows: unknown[]) =>
      rows.map((value) => {
        const row = rowSchema.parse(value);
        return Object.fromEntries(
          Object.entries(columns).map(([property, column]) => [
            property,
            row[column.name] === null || row[column.name] === undefined
              ? null
              : column.mapFromDriverValue(
                  getSqliteCodecs().apply(column, "normalize", row[column.name]),
                ),
          ]),
        );
      }),
    key,
    name: getTableName(table),
    parse: (rows: unknown) => rowsSchema.parse(schema.parse(rows)),
  };
});
